import { v4 as uuidv4 } from 'uuid';
import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';

const ENV_VARS = SessionStorage.get(AppStorageKeys.envVars);
const VERSION = ENV_VARS.VERSION;
const METRICS_SEND_INTERVAL = ENV_VARS.VIDEO_CALL_METRICS_REPORTINGINTERVAL_SEC || 5;

const USER_AGENT = navigator.userAgent;

async function getBrowserMicAndCameraPermissions() {
  const micStatus = await navigator.permissions.query({ name: 'microphone' });
  const camStatus = await navigator.permissions.query({ name: 'camera' });

  function mapPermission(state) {
    if (state === 'granted') return 'allowed';
    if (state === 'denied') return 'disallowed';
    return 'unknown';
  }

  return {
    mic_permission: mapPermission(micStatus.state),
    camera_permission: mapPermission(camStatus.state),
  };
}

// General functions
const generateTelemetryEvent = (eventType, eventPayload) => {
  return {
    message_type: eventType,
    message_id: uuidv4(),
    utc_timestamp_inms: Date.now(),
    source: USER_AGENT,
    app_version: VERSION,
    [eventType]: eventPayload,
  };
};

const sendTelemetryEvent = (sendMessage, eventType, eventPayload) => {
  const telemetryEvent = generateTelemetryEvent(eventType, eventPayload);
  sendMessage(telemetryEvent, 'message', 'events');
};

// Video call attendee info functions
export const sendAttendeeInfo = (sendMessage, attendeeInfoPayload) => {
  sendTelemetryEvent(sendMessage, 'videocall_attendeeinfo_event', attendeeInfoPayload);
};
let lastReport = null;

// Video call metrics functions
const getChimeMetrics = async (clientMetricReport) => {
  const stats = clientMetricReport.getRTCStatsReport();
  const browserPermissions = await getBrowserMicAndCameraPermissions();
  const metrics = {
    audioReceivePacketLossPercent: null,
    audioSendPacketLossPercent: null,
    videoAvailableSendBandwidth: null,
    videoAvailableReceiveBandwidth: null,
    videoSendBitrate: null,
    videoSendFps: null,
    browserMicPermission: browserPermissions.mic_permission,
    browserCameraPermission: browserPermissions.camera_permission,
  };

  if (stats) {
    stats.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.kind === 'audio') {
        metrics.audioReceivePacketLossPercent =
          report.packetsReceived > 0 ? (report.packetsLost / report.packetsReceived) * 100 : null;
        metrics.audioReceivePacketLossPercent = isNaN(metrics.audioReceivePacketLossPercent)
          ? null
          : metrics.audioReceivePacketLossPercent;
      }
      if (report.type === 'outbound-rtp' && report.kind === 'audio') {
        metrics.audioSendPacketLossPercent =
          report.packetsSent > 0 ? (report.packetsLost / report.packetsSent) * 100 : null;
        metrics.audioSendPacketLossPercent = isNaN(metrics.audioSendPacketLossPercent)
          ? null
          : metrics.audioSendPacketLossPercent;
      }
      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        metrics.videoAvailableSendBandwidth = report.availableOutgoingBitrate || null;
        metrics.videoAvailableReceiveBandwidth = report.availableIncomingBitrate || null;
      }
      if (report.type === 'outbound-rtp' && report.kind === 'video') {
        if (!lastReport) {
          lastReport = report;
          return;
        }
        if (report.timestamp > lastReport.timestamp && report.bytesSent >= lastReport.bytesSent) {
          const timeDiff = (report.timestamp - lastReport.timestamp) / 1000; // Convert ms to sec
          const bytesDiff = report.bytesSent - lastReport.bytesSent;

          metrics.videoSendBitrate =
            timeDiff > 0 ? parseFloat(((bytesDiff * 8) / (timeDiff * 1000)).toFixed(2)) : null;
        }
        metrics.videoSendBitrate = isNaN(metrics.videoSendBitrate)
          ? null
          : metrics.videoSendBitrate;
        metrics.videoSendFps = report.framesPerSecond || null;
        // Store current report for next calculation
        lastReport = report;
      }
    });
  }

  return metrics;
};

const constructMetricsPayload = (metrics) => {
  return {
    audio_receive_packet_loss_percent: metrics.audioReceivePacketLossPercent,
    audio_send_packet_loss_percent: metrics.audioSendPacketLossPercent,
    video_available_send_bandwidth: metrics.videoAvailableSendBandwidth,
    video_available_receive_bandwidth: metrics.videoAvailableReceiveBandwidth,
    video_send_bitrate: metrics.videoSendBitrate,
    video_send_fps: metrics.videoSendFps,
    mic_permissions: metrics.browserMicPermission,
    camera_permissions: metrics.browserCameraPermission,
  };
};

const sendVideoCallMetricsEvent = (sendMessage, metricsPayload) => {
  sendTelemetryEvent(sendMessage, 'videocall_metrics_event', metricsPayload);
};

let lastMetricsSentTime = 0;

export const sendMetricsInfo = (audioVideo, sendMessage) => {
  audioVideo.audioVideoController.addObserver({
    metricsDidReceive: (clientMetricReport) => {
      const fetchAndSendMetrics = async () => {
        const currentTime = Date.now();
        //sendVideoCallMetricsEvent function is called with a delay (5000 ms) default to limit the frequency of its execution.
        if (currentTime - lastMetricsSentTime < METRICS_SEND_INTERVAL * 1000) {
          return;
        }
        lastMetricsSentTime = currentTime;

        try {
          const metrics = await getChimeMetrics(clientMetricReport);
          const payload = constructMetricsPayload(metrics);
          sendVideoCallMetricsEvent(sendMessage, payload);
        } catch (error) {
          console.error('Error fetching metrics:', error);
        }
      };

      fetchAndSendMetrics();
    },
  });
};

// Video call info functions
const getChimeInfo = async (audioVideo) => {
  if (!audioVideo) {
    throw new Error('audioVideo object is required');
  }

  try {
    const audioInputDevices = await audioVideo.listAudioInputDevices();
    const videoInputDevices = await audioVideo.listVideoInputDevices();
    const audio_streamtype = audioInputDevices.length > 0 ? 'VoiceCall' : 'None';
    const audio_mode = audioInputDevices.some((device) => device.label.includes('Stereo'))
      ? 'Stereo48K'
      : 'Mono';

    const selfview_onoff = audioVideo.getLocalVideoTile() !== null;
    const mic_onoff = !audioVideo.realtimeIsLocalAudioMuted();
    const audio_session_status = audioVideo.realtimeIsLocalAudioMuted() ? 'Muted' : 'Started';

    const videoQualitySettings = audioVideo.getVideoInputQualitySettings();
    const videoencoding_resolution = videoQualitySettings
      ? `${videoQualitySettings.videoWidth}x${videoQualitySettings.videoHeight}p${videoQualitySettings.videoFrameRate}`
      : 'Unknown';
    const video_session_status = audioVideo.hasStartedLocalVideoTile() ? 'Started' : 'Stopped';

    return {
      audio_streamtype,
      audio_mode,
      selfview_onoff,
      mic_onoff,
      audio_session_status,
      videoencoding_resolution,
      video_session_status,
    };
  } catch (error) {
    console.error('Error getting Chime info:', error);
    throw error;
  }
};

const constructInfoPayload = (info) => {
  return {
    audio_streamtype: info.audio_streamtype,
    audio_mode: info.audio_mode,
    selfview_onoff: info.selfview_onoff,
    mic_onoff: info.mic_onoff,
    audio_session_status: info.audio_session_status,
    videoencoding_resolution: info.videoencoding_resolution,
    video_session_status: info.video_session_status,
  };
};

const sendVideoCallInfoEvent = (sendMessage, infoPayload) => {
  sendTelemetryEvent(sendMessage, 'videocall_info_event', infoPayload);
};

export const sendCallInfo = async (audioVideo, sendMessage) => {
  const info = await getChimeInfo(audioVideo);
  const payload = constructInfoPayload(info);
  sendVideoCallInfoEvent(sendMessage, payload);
};
